/*
 * Plan Builder action library + matcher — ported VERBATIM from
 * artifacts/prototypes/motr-improvement-plan-v1.html.
 *
 * The library, the miracle lexicon, and the matcher's scoring +
 * dedup-by-lever logic are all identical to the prototype. Do not
 * re-tune the math here — any change should land in both surfaces
 * simultaneously.
 *
 * RESET_LEVERS, BLOCKERS, and PREMORTEM have moved to
 * DEFAULT_PLAN_BUILDER_CONFIG in config.ts (round A admin surface).
 * ACTIONS, miracle lexicons, and match-scoring weights stay here as
 * module-level consts (round B).
 *
 * Origin lines in prototype (for diff anchoring):
 *   ACTIONS[]                   ~2533–2688
 *   RESET_LEVERS                ~2501–2513
 *   BLOCKERS                    ~2516–2523
 *   PREMORTEM                   ~2804–2829
 *   MIRACLE_*                   ~2694–2715
 *   extractMiracleThemes        ~2717–2731
 *   matchActions                ~2735–2802
 */
import type {
  ActionDef,
  DiagnosticAnswers,
  IhiDriver,
  MatchedAction,
  ResetLever,
  ScoreResult,
} from './types';
import type { PlanBuilderConfig } from './config';

// ─── Action library ─────────────────────────────────────────────────
// Each action is tagged with:
//   resetLever — which of the 11 Reset strategies it embodies
//   targets    — diagnostic outputs it suits (drivers / stages / blockers / preMortems)
//   effort     — 'low' | 'medium' | 'high'
//   horizon    — 'burst' | '30 days' | '90 days' | 'ongoing'
// The matcher scores each action against a diagnostic result and picks
// the top N, deduplicated by Reset lever so the plan covers breadth.
export const ACTIONS: ReadonlyArray<ActionDef> = [
  // AUTONOMY ────────────────────────────────────────────────────
  {
    id: 'stop-start-less-more',
    resetLever: 'do_less_do_more',
    title: 'Run a STOP/START/LESS/MORE exercise',
    body: 'Run a STOP/START/LESS/MORE exercise with the team to identify low-value work that can be eliminated or delegated — freeing space for higher-agency work.',
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['autonomy'],
      stages: [1, 2, 3],
      blockers: ['workload', 'clarity'],
      preMortems: ['capacity', 'scope'],
    },
    effort: 'low',
    horizon: '30 days',
  },
  {
    id: 'intake-operating-model',
    resetLever: 'map_the_system',
    title: 'Define an intake operating model the team co-designs',
    body: 'Establish a defined intake operating model that the team co-designs, so lawyers control how requests enter and are prioritised rather than being subject to whoever escalates loudest.',
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['autonomy', 'participative'],
      stages: [1, 2],
      blockers: ['workload', 'process'],
      preMortems: [],
    },
    effort: 'medium',
    horizon: '90 days',
  },
  {
    id: 'legal-ops-config-ownership',
    resetLever: 'let_people_drive',
    title: 'Give Legal Ops owned configuration authority',
    body: 'Give Legal Ops explicit ownership of at least one system configuration decision per quarter — visible authority, not just advisory input.',
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['autonomy'],
      stages: [2, 3],
      blockers: ['tools'],
      preMortems: [],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  // SAFETY ──────────────────────────────────────────────────────
  {
    id: 'whats-not-working-opener',
    resetLever: 'go_and_see',
    title: 'Open team meetings with "what’s not working?"',
    body: "Start team meetings with a deliberate \"what's not working?\" question — and make it the leader's job to take notes, not defend.",
    research: 'Amy Edmondson, The Fearless Organization (2018)',
    targets: {
      drivers: ['safety'],
      stages: [1, 2, 3, 4],
      blockers: ['culture'],
      preMortems: ['buyin'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'anonymous-pulse',
    resetLever: 'accelerate_learning',
    title: 'Run a lightweight monthly anonymous pulse',
    body: 'Use a lightweight anonymous pulse (3 questions, monthly) so people can surface concerns without attribution. Publish the results to the team — transparency about progress is itself a driver of motivation.',
    research:
      'Amy Edmondson, The Fearless Organization (2018); Pfeffer & Sutton EBM',
    targets: {
      drivers: ['safety'],
      stages: [2, 3, 4],
      blockers: ['culture', 'visibility'],
      preMortems: ['measurement'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'system-before-blame',
    resetLever: 'go_and_see',
    title: 'Lead with curiosity, not accountability, when things fail',
    body: 'When something goes wrong, lead with curiosity not accountability — "what did the system make hard?" before "who dropped the ball?"',
    research: 'Amy Edmondson, The Fearless Organization (2018)',
    targets: {
      drivers: ['safety'],
      stages: [1, 2, 3, 4],
      blockers: ['culture'],
      preMortems: [],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  // MEANING ─────────────────────────────────────────────────────
  {
    id: 'close-the-loop',
    resetLever: 'bright_spots',
    title: 'Close the loop on legal work outcomes',
    body: 'Close the loop on legal work — brief the team on what happened with the contracts, disputes, or advice they worked on. Work that disappears into a void feels like toil; work whose outcome the team can see feels like contribution.',
    research: 'Self-Determination Theory, Deci & Ryan (2017)',
    targets: {
      drivers: ['meaning'],
      stages: [2, 3, 4],
      blockers: ['visibility', 'clarity'],
      preMortems: [],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'win-log',
    resetLever: 'bright_spots',
    title: 'Start a monthly win-log of legal impact',
    body: "Create a simple win-log: a shared record of legal's contribution to business outcomes, updated monthly.",
    research: 'Self-Determination Theory, Deci & Ryan (2017)',
    targets: {
      drivers: ['meaning', 'recognition'],
      stages: [1, 2, 3],
      blockers: ['visibility'],
      preMortems: [],
    },
    effort: 'low',
    horizon: '30 days',
  },
  {
    id: 'business-context-framing',
    resetLever: 'goal_of_goal',
    title: 'Frame team meetings around quarterly business context',
    body: 'Frame team meetings around business context — what is the business trying to do this quarter and how does our work connect to it?',
    research: 'Self-Determination Theory, Deci & Ryan (2017)',
    targets: {
      drivers: ['meaning'],
      stages: [2, 3, 4],
      blockers: ['clarity'],
      preMortems: [],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  // PARTICIPATIVE ──────────────────────────────────────────────
  {
    id: 'what-matters-1-1s',
    resetLever: 'tap_motivation',
    title: 'Run structured "what matters to you?" 1:1s',
    body: 'Ask the team "what matters to you?" in a structured 1:1 format — not as a survey, as a genuine conversation with notes and follow-up.',
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['participative', 'meaning'],
      stages: [1, 2, 3, 4],
      blockers: ['clarity'],
      preMortems: ['buyin'],
    },
    effort: 'low',
    horizon: '30 days',
  },
  {
    id: 'co-design-changes',
    resetLever: 'let_people_drive',
    title: 'Involve two team members in every process change',
    body: 'Before changing any team process, involve at least two team members in designing the change. People support what they help build.',
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['participative'],
      stages: [1, 2, 3, 4],
      blockers: ['culture'],
      preMortems: ['buyin'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'mission-review',
    resetLever: 'goal_of_goal',
    title: 'Create a lightweight mission-review mechanism',
    body: "Create a standing way for team members to flag decisions that feel misaligned with how legal should operate. A mission-review slot keeps the function honest about what it's actually optimising for.",
    research: 'IHI Framework for Improving Joy in Work (Perlo et al., 2017)',
    targets: {
      drivers: ['participative'],
      stages: [3, 4],
      blockers: ['clarity'],
      preMortems: [],
    },
    effort: 'medium',
    horizon: '90 days',
  },
  // IMPROVEMENT ────────────────────────────────────────────────
  {
    id: 'protected-improvement-hour',
    resetLever: 'start_with_burst',
    title: 'Block one protected improvement hour per person per week',
    body: 'Block one hour per week per person as protected improvement time — not billable, not interruptible. Without protected time, improvement always loses to the urgent.',
    research: "Dan Heath, Reset: How to Change What's Not Working (2025)",
    targets: {
      drivers: ['improvement'],
      stages: [2, 3, 4],
      blockers: ['workload'],
      preMortems: ['capacity'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: '30-day-burst-sprint',
    resetLever: 'start_with_burst',
    title: 'Run a 30-day burst sprint on one named friction point',
    body: 'Pick one pebble (a specific friction point the team has named), fix it visibly in 30 days, and announce the before/after. A visible win in 30 days builds more credibility than any strategy document.',
    research: "Dan Heath, Reset: How to Change What's Not Working (2025)",
    targets: {
      drivers: ['improvement'],
      stages: [1, 2, 3],
      blockers: ['process', 'tools'],
      preMortems: ['buyin', 'scope'],
    },
    effort: 'medium',
    horizon: '30 days',
  },
  {
    id: 'downtime-audit',
    resetLever: 'recycle_waste',
    title: 'Run a quarterly DOWNTIME audit on one workflow',
    body: "Use Heath's DOWNTIME lens (Defects, Overproduction, Waiting, Non-utilised talent, Transport, Inventory, Motion, Excess processing) to audit one workflow per quarter: where is time being lost?",
    research: 'Dan Heath, Reset (2025); Toyota Production System',
    targets: {
      drivers: ['improvement'],
      stages: [2, 3],
      blockers: ['workload', 'process'],
      preMortems: ['capacity'],
    },
    effort: 'medium',
    horizon: '90 days',
  },
  // RECOGNITION ────────────────────────────────────────────────
  {
    id: 'outcome-metrics-reporting',
    resetLever: 'bright_spots',
    title: 'Shift leadership reporting from volume to outcomes',
    body: "Build legal's impact into leadership reporting — not just volume metrics (matters opened/closed) but outcome metrics management cares about (revenue enabled, risk mitigated, cycle time compressed).",
    research: 'Gallup State of the Global Workplace (2026)',
    targets: {
      drivers: ['recognition'],
      stages: [2, 3, 4],
      blockers: ['visibility'],
      preMortems: ['measurement'],
    },
    effort: 'medium',
    horizon: '90 days',
  },
  {
    id: 'monthly-gc-impact-summary',
    resetLever: 'bright_spots',
    title: 'Write a monthly impact summary for the GC or CLO',
    body: "One page. Consistent format. What the team did, what it enabled, what risk it mitigated. Leadership that can see legal’s impact funds it differently.",
    research: 'Gallup State of the Global Workplace (2026)',
    targets: {
      drivers: ['recognition'],
      stages: [1, 2, 3],
      blockers: ['visibility'],
      preMortems: ['leadership'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'specific-public-recognition',
    resetLever: 'tap_motivation',
    title: 'Acknowledge specific contributions publicly by name',
    body: "Name the person, the work, and the outcome it enabled. Generic \"great team effort\" doesn't land; specific named recognition does.",
    research: 'Gallup State of the Global Workplace (2026)',
    targets: {
      drivers: ['recognition'],
      stages: [1, 2, 3, 4],
      blockers: [],
      preMortems: ['buyin'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  // COVERAGE ADDITIONS — target_constraint, map_the_system, do_less_do_more
  {
    id: 'wip-limits',
    resetLever: 'do_less_do_more',
    title: 'Set a hard cap on concurrent initiatives',
    body: "Agree a maximum number of concurrent improvement initiatives (a WIP limit). Nothing new starts until something finishes. Resist the urge to fix everything — it's how nothing gets fixed.",
    research: 'Dan Heath, Reset (2025); Lean / Kanban',
    targets: {
      drivers: ['improvement'],
      stages: [2, 3],
      blockers: ['workload', 'clarity'],
      preMortems: ['scope', 'capacity'],
    },
    effort: 'low',
    horizon: 'ongoing',
  },
  {
    id: 'single-source-dashboard',
    resetLever: 'map_the_system',
    title: 'Build one glance-view dashboard leadership will actually read',
    body: "Build a single-source dashboard that leadership can read in under 60 seconds — matters in flight, capacity status, top risks. One tab. No configuration. Anchor the change to a metric on leadership's dashboard from day one.",
    research: 'Pfeffer & Sutton, Hard Facts, Dangerous Half-Truths (2006)',
    targets: {
      drivers: ['recognition', 'autonomy'],
      stages: [2, 3],
      blockers: ['visibility', 'tools'],
      preMortems: ['measurement', 'leadership'],
    },
    effort: 'medium',
    horizon: '90 days',
  },
  {
    id: 'constraint-audit',
    resetLever: 'target_constraint',
    title: 'Audit your highest-frequency workflow for its #1 constraint',
    body: 'Pick the single workflow that consumes the most team hours. Map it end to end. Name the one step where work piles up or stalls. That constraint — not everything around it — is where you should push first.',
    research: 'Dan Heath, Reset (2025); Theory of Constraints (Goldratt)',
    targets: {
      drivers: ['improvement', 'autonomy'],
      stages: [1, 2, 3],
      blockers: ['workload', 'process'],
      preMortems: ['scope'],
    },
    effort: 'medium',
    horizon: '30 days',
  },
];

/** Total number of actions in the library. Read-only display in the admin panel. */
export const ACTION_COUNT = ACTIONS.length;

// ─── Miracle Question lexicons ──────────────────────────────────────
// Words/phrases that, when found in the user's free-text Miracle
// answers, hint at which Reset lever or IHI driver an action should
// boost. Editable in the admin's Action Library tab once the backend
// admin is wired (out of scope for this round).
const MIRACLE_LEVER_LEXICON: Record<ResetLever, string[]> = {
  bright_spots: [
    'visibility',
    'visible',
    'see',
    'seen',
    'noticed',
    'recognition',
    'recognised',
    'recognized',
    'thanked',
    'credit',
    'reported',
    'dashboard',
    'metric',
    'measured',
    'tracked',
    'leadership notices',
  ],
  map_the_system: [
    'dashboard',
    'overview',
    'at a glance',
    'pipeline',
    'real-time',
    'status',
    'where everything is',
    "who's working on what",
  ],
  target_constraint: [
    'queue',
    'backlog',
    'flow',
    'unblocked',
    'unstuck',
    'moving',
    'no longer waiting',
    'bottleneck',
    'stalled',
    'contracts moving',
  ],
  recycle_waste: [
    'time',
    'free',
    'space',
    'breathing',
    'breathe',
    'headroom',
    'less rework',
    'stopped chasing',
    'inbox',
    'clutter',
    'efficient',
    'clean',
  ],
  do_less_do_more: [
    'priorities',
    'priority',
    'focused',
    'stopped doing',
    'not doing',
    'said no',
    'declined',
    'scope',
    'clearer',
    'fewer things',
    'simpler',
  ],
  goal_of_goal: [
    'outcomes',
    'business outcome',
    'mission',
    'purpose',
    'why',
    'making a difference',
    'connected',
    'matters',
    'meaningful',
  ],
  start_with_burst: [
    'focus',
    'deep work',
    'concentrated',
    'block of time',
    'protected time',
    'thinking time',
    'momentum',
    'real work',
  ],
  tap_motivation: [
    'team',
    'morale',
    'happier',
    'smile',
    'energy',
    'engaged',
    'less stressed',
    'calmer',
    'lighter',
    'enjoying',
  ],
  let_people_drive: [
    'autonomy',
    'their own',
    'ownership',
    'driving',
    'leading',
    'choosing',
    'designing',
    'co-design',
    'taking charge',
  ],
  accelerate_learning: [
    'feedback',
    'pulse',
    'survey',
    'check-in',
    'asking',
    'listening',
    'learning',
    'iterating',
    'evolving',
  ],
  go_and_see: [
    'observed',
    'walked through',
    'shadowed',
    'sat with',
    'listened to',
    'in the work',
  ],
};

const MIRACLE_DRIVER_LEXICON: Record<IhiDriver, string[]> = {
  autonomy: ['autonomy', 'their own', 'choose', 'decide', 'control', 'agency', 'freedom'],
  safety: [
    'safe',
    'speak up',
    'honest',
    'open',
    'trust',
    'comfortable',
    'no fear',
    'transparent',
    'candid',
  ],
  meaning: [
    'purpose',
    'matters',
    'meaningful',
    'making a difference',
    'impact',
    'why',
    'mission',
  ],
  participative: ['involved', 'consulted', 'asked', 'co-design', 'shaping', 'voice', 'included'],
  improvement: ['better', 'improving', 'iterating', 'fixing', 'experimenting', 'learning', 'evolving'],
  recognition: [
    'recognised',
    'recognized',
    'noticed',
    'thanked',
    'credit',
    'appreciated',
    'acknowledged',
  ],
};

// Cap on the lexicon-driven boost so themes never overpower the core
// stage / blocker / driver matching.
const MIRACLE_BOOST_CAP = 2;

export interface MiracleThemes {
  levers: Set<ResetLever>;
  drivers: Set<IhiDriver>;
  filled: number;
}

export function extractMiracleThemes(
  answers: Pick<DiagnosticAnswers, 'miracle_desk' | 'miracle_midday' | 'miracle_gc'>,
): MiracleThemes {
  const text = [answers.miracle_desk, answers.miracle_midday, answers.miracle_gc]
    .filter((s): s is string => Boolean(s))
    .join(' \n ')
    .toLowerCase();
  const filled = (
    ['miracle_desk', 'miracle_midday', 'miracle_gc'] as const
  ).filter((k) => answers[k] && String(answers[k]).trim()).length;
  if (!text.trim()) {
    return { levers: new Set(), drivers: new Set(), filled };
  }
  const levers = new Set<ResetLever>();
  const drivers = new Set<IhiDriver>();
  (Object.entries(MIRACLE_LEVER_LEXICON) as Array<[ResetLever, string[]]>).forEach(
    ([lever, words]) => {
      if (words.some((w) => text.indexOf(w.toLowerCase()) !== -1)) levers.add(lever);
    },
  );
  (
    Object.entries(MIRACLE_DRIVER_LEXICON) as Array<[IhiDriver, string[]]>
  ).forEach(([driver, words]) => {
    if (words.some((w) => text.indexOf(w.toLowerCase()) !== -1)) drivers.add(driver);
  });
  return { levers, drivers, filled };
}

const EFFORT_RANK: Record<ActionDef['effort'], number> = {
  low: 1,
  medium: 2,
  high: 3,
};

export interface MatchOpts {
  limit?: number;
}

/**
 * Score every action in the library against the diagnostic result,
 * sort, then dedupe by Reset lever so the plan covers breadth (one
 * lever per action). Tops up if dedup left us short.
 */
export function matchActions(
  scoreResult: ScoreResult,
  answers: DiagnosticAnswers,
  config: PlanBuilderConfig,
  opts: MatchOpts = {},
): MatchedAction[] {
  const limit = opts.limit ?? 4;
  const stage = scoreResult.stage;
  const weakest = scoreResult.weakest;
  const blocker = answers.gap2 ?? null;
  const preMortem = scoreResult.preMortem;
  const themes = extractMiracleThemes(answers);

  const scored: MatchedAction[] = ACTIONS.map((a): MatchedAction => {
    let score = 0;
    const reasons: string[] = [];
    const driverHits = weakest.filter((d) => a.targets.drivers.includes(d));
    if (driverHits.length) {
      score += 3 * driverHits.length;
      reasons.push(
        'addresses ' + driverHits.map((d) => config.scoring.ihiLabels[d]).join(' + '),
      );
    }
    if (stage && a.targets.stages.includes(stage)) {
      score += 2;
      reasons.push('fits Stage ' + stage);
    }
    if (blocker && a.targets.blockers.includes(blocker)) {
      score += 2;
      const b = config.planEngine.blockers[blocker];
      reasons.push('tackles the ' + b.label.toLowerCase() + ' blocker');
    }
    if (preMortem && a.targets.preMortems.includes(preMortem)) {
      score += 2;
      reasons.push('mitigates the ' + preMortem + ' failure mode');
    }
    let miracleBoost = 0;
    if (themes.levers.has(a.resetLever)) miracleBoost += 1;
    const driverThemeHits = a.targets.drivers.filter((d) => themes.drivers.has(d)).length;
    if (driverThemeHits) miracleBoost += 1;
    if (miracleBoost > 0) {
      const capped = Math.min(miracleBoost, MIRACLE_BOOST_CAP);
      score += capped;
      reasons.push('echoes what you described as the morning after');
    }
    return { action: a, score, reasons };
  }).filter((s) => s.score > 0);

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return EFFORT_RANK[a.action.effort] - EFFORT_RANK[b.action.effort];
  });

  const seenLever = new Set<ResetLever>();
  const picked: MatchedAction[] = [];
  for (const s of scored) {
    if (seenLever.has(s.action.resetLever)) continue;
    picked.push(s);
    seenLever.add(s.action.resetLever);
    if (picked.length >= limit) break;
  }
  if (picked.length < limit) {
    for (const s of scored) {
      if (picked.indexOf(s) !== -1) continue;
      picked.push(s);
      if (picked.length >= limit) break;
    }
  }
  return picked;
}
